export default {
  id: 'reading-results',
  title: 'Reading Backtest Results',
  content: `## Reading Backtest Results

The results screen shows you a complete performance summary of how your strategy would have performed over the selected historical period.

---

## Summary Metrics

| Metric | What it means |
|---|---|
| Total signals | How many times the strategy conditions were met |
| Total trades | How many simulated trades were placed (signals × symbols) |
| Win rate | % of trades that hit target before stop |
| Avg win | Average profit on winning trades (₹ and %) |
| Avg loss | Average loss on losing trades (₹ and %) |
| Profit factor | Total gross profit ÷ total gross loss (> 1.0 = profitable) |
| Avg R:R achieved | Average reward:risk ratio on all completed trades |
| Max drawdown | Largest peak-to-trough decline in cumulative P&L |
| Total P&L | Net profit or loss across all simulated trades |

---

## Key Numbers to Focus On

### Profit Factor

**Profit Factor = Total Wins ÷ Total Losses**

| Value | What it means |
|---|---|
| > 2.0 | Strong edge |
| 1.3–2.0 | Viable — worth testing live with small size |
| 1.0–1.3 | Marginal — costs and slippage will likely eliminate edge |
| < 1.0 | The strategy lost money in this period |

---

### Win Rate vs Average R:R

These two numbers must be read together:

| Win Rate | Avg R:R | Viability |
|---|---|---|
| 60% | 1.5:1 | Viable |
| 40% | 2.5:1 | Viable |
| 30% | 3.0:1 | Viable |
| 50% | 0.8:1 | Not viable — losses outsize wins |

A low win rate with a high R:R is a perfectly valid strategy. A high win rate with a low R:R usually fails once a few large losses appear.

---

### Max Drawdown

Max drawdown tells you the worst sequence of losses the strategy went through.

**Example:** Max drawdown of 15% on a ₹10,00,000 account = the strategy would have drawn your account down by ₹1,50,000 at its worst point before recovering.

If the max drawdown is larger than you could emotionally or financially tolerate, the strategy needs to be revised — either by tightening conditions or reducing position size.

---

## Trade-by-Trade Log

Below the summary, every simulated trade is listed:

| Column | Description |
|---|---|
| Symbol | Stock the trade was on |
| Entry date | When conditions triggered |
| Entry price | Simulated fill price |
| Exit date | When stop, target, or max hold was reached |
| Exit price | Simulated exit price |
| Outcome | WIN / LOSS / TIMEOUT |
| P&L | Profit or loss on this trade |

Review losing trades — do they cluster around specific stocks, specific market conditions, or a particular date range? Patterns in losses reveal where the strategy is weakest.

---

## What to Do With the Results

**If Profit Factor > 1.5 and drawdown is acceptable:**
→ Consider activating this as an Algo Flow strategy and running it live for 2–4 weeks with a small position size to validate on real-time data.

**If win rate is good but drawdown is too high:**
→ Tighten your stop loss %, or add a trend filter (e.g., UPTREND only) to avoid entries in weak market conditions.

**If signals are too frequent:**
→ Add more restrictive conditions (AND logic, tighter thresholds) to increase selectivity.

**If too few signals:**
→ Loosen one condition, extend the date range, or add more symbols.`,
};
